import {
	BarChartOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	DollarOutlined,
	SendOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Select, Spin } from "antd";
import { useState } from "react";
import { SmsNavigation } from "../components/SmsNavigation";
import { getSmsReports } from "../services/sms.api";

function formatNum(n: number): string {
	const abs = Math.abs(n);
	const hasFraction = abs % 1 !== 0;
	const isSmall = abs < 1000;
	const formatted =
		hasFraction || isSmall
			? abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
			: abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	return n < 0 ? `\u2212${formatted}` : formatted;
}

const periodOptions = [
	{ label: "Kunlik", value: "day" as const },
	{ label: "Haftalik", value: "week" as const },
	{ label: "Oylik", value: "month" as const },
];

export function SmsReportsPage() {
	const [period, setPeriod] = useState<"day" | "week" | "month">("month");

	const { data: report, isLoading } = useQuery({
		queryKey: ["smsReports", period],
		queryFn: () => getSmsReports({ period }),
	});

	if (isLoading) {
		return (
			<div style={{ textAlign: "center", padding: "100px 0" }}>
				<Spin size="large" />
				<div style={{ marginTop: "16px", color: "var(--crm-ink-3)" }}>
					SMS hisobot yuklanmoqda...
				</div>
			</div>
		);
	}

	const totalSent = report?.totalSent ?? 0;
	const totalDelivered = report?.totalDelivered ?? 0;
	const totalFailed = report?.totalFailed ?? 0;
	const totalCost = report?.totalCostUzs ?? 0;
	const deliveryRate = report?.deliveryRate ?? 0;

	return (
		<div className="crm-sms-reports">
			{/* Page Head */}
			<div className="crm-page-head" style={{ marginBottom: "16px" }}>
				<div>
					<h1>SMS hisobotlari</h1>
					<p>Yetkazilish va xarajat statistikasi</p>
				</div>
			</div>

			{/* Sub-Navigation Tabs */}
			<SmsNavigation activeTab="reports" />

			{/* Period Filter */}
			<div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
				<span style={{ fontWeight: 600, color: "var(--crm-ink-2)" }}>Davr:</span>
				<Select
					value={period}
					onChange={setPeriod}
					options={periodOptions}
					style={{ width: 160 }}
				/>
			</div>

			{/* KPI Grid */}
			<div className="crm-kpi-grid crm-sms-kpi-grid">
				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon blue">
							<SendOutlined />
						</span>
						Jami yuborilgan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatNum(totalSent)}</strong>
						<span>ta</span>
					</div>
				</div>

				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon green">
							<CheckCircleOutlined />
						</span>
						Etkazilgan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatNum(totalDelivered)}</strong>
						<span>ta</span>
					</div>
					<div className="crm-kpi-note">{deliveryRate}% delivery rate</div>
				</div>

				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon" style={{ background: "#fff1f0", color: "#cf1322" }}>
							<CloseCircleOutlined />
						</span>
						Muvaffaqiyatsiz
					</div>
					<div className="crm-kpi-value">
						<strong style={{ color: totalFailed > 0 ? "#cf1322" : undefined }}>
							{formatNum(totalFailed)}
						</strong>
						<span>ta</span>
					</div>
				</div>

				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<DollarOutlined />
						</span>
						Jami xarajat
					</div>
					<div className="crm-kpi-value">
						<strong>{formatNum(totalCost)}</strong>
						<span>so'm</span>
					</div>
				</div>
			</div>

			{/* Summary Table */}
			<div className="crm-card crm-table-card crm-sms-table-card" style={{ marginTop: "20px" }}>
				<div className="crm-card-head">
					<h3>
						<BarChartOutlined style={{ marginRight: 8 }} />
						Umumiy ko'rsatkichlar
					</h3>
				</div>
				<div className="crm-table-wrap">
					<table className="crm-table crm-summary-table">
						<thead>
							<tr>
								<th>Ko'rsatkich</th>
								<th style={{ textAlign: "right" }}>Qiymat</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Jami yuborilgan SMS</td>
								<td style={{ textAlign: "right" }}>
									<span className="num">{formatNum(totalSent)}</span>
								</td>
							</tr>
							<tr>
								<td>Muvaffaqiyatli etkazilgan</td>
								<td style={{ textAlign: "right" }}>
									<span className="num" style={{ color: "#389e0d" }}>
										{formatNum(totalDelivered)}
									</span>
								</td>
							</tr>
							<tr>
								<td>Muvaffaqiyatsiz</td>
								<td style={{ textAlign: "right" }}>
									<span className="num" style={{ color: "#cf1322" }}>
										{formatNum(totalFailed)}
									</span>
								</td>
							</tr>
							<tr>
								<td>Etkazilish foizi (Delivery Rate)</td>
								<td style={{ textAlign: "right" }}>
									<span className="num" style={{ color: "#2474d6" }}>
										{deliveryRate}%
									</span>
								</td>
							</tr>
							<tr>
								<td>Jami xarajat</td>
								<td style={{ textAlign: "right" }}>
									<span className="num">{formatNum(totalCost)} so'm</span>
								</td>
							</tr>
							<tr>
								<td>Davr</td>
								<td style={{ textAlign: "right" }}>
									<span className="crm-badge blue">
										{report?.period === "day"
											? "Kunlik"
											: report?.period === "week"
												? "Haftalik"
												: "Oylik"}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

export default SmsReportsPage;
